import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PhoneStar } from '../../../../../../shared/svg/phone-star';
import { validate } from './validator';
import Select from '../../../../../../shared/design-system/components/select';
import Input from '../../../../../../shared/design-system/components/input';
import Modal from '../../../../../../shared/design-system/components/atoms/modal';
import { capitalize } from '../../../../../../shared/utils';
import { Images } from '../../../../../../shared/app-constants';
import {
  OutcomeSentiment,
  OutcomeSentimentBGColor,
} from '../../../../enums/custom-outcome';
import { CallOutcome, CreateUpdateCallOutcomeModalData } from '../../types';

type DropdownOption = {
  name: OutcomeSentiment;
  key: OutcomeSentiment;
};

const sentimentOptions: DropdownOption[] = [
  { name: OutcomeSentiment.Positive, key: OutcomeSentiment.Positive },
  { name: OutcomeSentiment.Negative, key: OutcomeSentiment.Negative },
  { name: OutcomeSentiment.Neutral, key: OutcomeSentiment.Neutral },
];

type CreateUpdateCallOutcomeModalProps = {
  show: boolean;
  modalHeaderTitle: string;
  customOutcome?: CallOutcome;
  onSave: (payload: CreateUpdateCallOutcomeModalData) => void;
  onClose: () => void;
  isRequestPending: boolean;
  disableDropdownBox?: boolean;
};

const CreateUpdateCallOutcomeModal: React.FC<
  CreateUpdateCallOutcomeModalProps
> = ({
  show,
  modalHeaderTitle,
  customOutcome,
  onSave,
  onClose,
  isRequestPending,
  disableDropdownBox = false,
}) => {
  const { t } = useTranslation();

  const [values, setValues] = useState({
    fieldName: customOutcome ? customOutcome.name : '',
    sentiment: customOutcome
      ? customOutcome.sentiment
      : OutcomeSentiment.Positive,
  });

  const [errors, setErrors] = useState({
    fieldName: '',
    sentiment: '',
  });

  const [dirty, setDirty] = useState({
    fieldName: false,
    sentiment: false,
  });

  const isDefault = customOutcome?.isDefault === 1;

  const onInputChange = (value: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setDirty((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validate(name, value) }));
  };

  const onInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    if (dirty[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: validate(name, values[name]),
      }));
    }
  };

  const onSelectChange = (option: DropdownOption) => {
    setValues((prev) => ({ ...prev, sentiment: option.key }));
    setDirty((prev) => ({ ...prev, sentiment: true }));
  };

  const isSubmitDisabled = () =>
    isRequestPending ||
    !(dirty.fieldName || dirty.sentiment) ||
    (values.fieldName === customOutcome?.name &&
      values.sentiment === customOutcome?.sentiment) ||
    !!errors.fieldName ||
    !!errors.sentiment;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const tempDirty = { ...dirty };
    const tempErrors = { ...errors };

    Object.keys(tempDirty).forEach((key) => {
      tempDirty[key] = true;
    });

    let isError = false;
    Object.keys(tempErrors).forEach((key) => {
      const keyValue = values[key]?.trim();
      const error = validate(key, keyValue);
      tempErrors[key] = error;
      isError = isError || !!error;
    });

    if (isError) {
      setErrors(tempErrors);
      setDirty(tempDirty);
      return;
    }

    const payload: CreateUpdateCallOutcomeModalData = {
      label: values.fieldName,
      sentiment: values.sentiment,
    };

    onSave(payload);
  };

  return (
    <Modal
      show={show}
      className="add-call-outcome-modal"
      titleContent={
        <div className="modal-header-custom">
          <div className="sidenav__icon">
            <PhoneStar width={24} height={24} />
          </div>
          <h4 className="ml-2">{modalHeaderTitle}</h4>
        </div>
      }
      onClose={onClose}
      onSubmit={onSubmit}
      showCloseIcon={false}
      cancelButtonText={t('Cancel')}
      submitButtonText={t('Save')}
      isSubmitLoading={isRequestPending}
      isSubmitDisabled={isSubmitDisabled()}
      backdrop="static"
      showSeparator={true}
      extraModalProps={{
        centered: true,
      }}
    >
      <div className="call-outcome-modal-body">
        <Input
          disabled={!!isDefault}
          name="fieldName"
          label={t('Outcome Name')}
          placeholder="ex: Demo Given"
          value={values.fieldName}
          size={Input.Size.Medium}
          variant={errors.fieldName && Input.Variant.Error}
          onChange={onInputChange}
          onBlur={onInputBlur}
        />
        <div>
          {errors.fieldName && (
            <span className="error-label">
              <img src={Images.AlertTriangleRed} alt="Alert" />
              {t(`messages.${errors.fieldName}`)}
            </span>
          )}
        </div>
        <span className="select-label">{t('Sentiment')}</span>{' '}
        <Select<DropdownOption>
          options={sentimentOptions}
          selectedOptionKey={values.sentiment}
          optionRenderer={(option) => {
            if (option) {
              return (
                <div className="sentiment-content align-items-center">
                  <div
                    className="sentiment-dot"
                    style={{
                      background: OutcomeSentimentBGColor[option.name],
                    }}
                  />
                  <div>{capitalize(option.name)}</div>
                </div>
              );
            }
            return '-';
          }}
          showOptionsSeparator={true}
          onChange={([option]) => {
            onSelectChange(option);
          }}
          selectedOptionRenderer={([option]) => {
            if (option) {
              return (
                <div className="sentiment-content align-items-center">
                  <div
                    className="sentiment-dot"
                    style={{
                      background: OutcomeSentimentBGColor[option.name],
                    }}
                  />
                  <div>{capitalize(option.name)}</div>
                </div>
              );
            }
            return '-';
          }}
          disabled={disableDropdownBox}
        />
      </div>
    </Modal>
  );
};

export default CreateUpdateCallOutcomeModal;
